import {
  completeQueryResult,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  of,
  pipe,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  Renderer2,
} from '@angular/core';

import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { ZalamoObject } from '@ui/orders/graphql';

import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { TabloFaceToPerson, TabloMember } from '@ui/tablo-generator/types';
import { GetImageUrlService } from 'ZalamoLibrary/src/lib/core/image/image-loader/get-image-url.service';
import { firstValueFrom } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { FaceStrongDetectionService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-strong-detection.service';
import { UntypedFormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TabloMemberPhotoPositionService } from './tablo-member-photo-position.service';
import {
  GetPublicSessionSignupCampaignGQL,
  GetPublicSessionSignupCampaignSubmissionsGQL,
  PublicSessionSignupCampaignFieldsFragment,
  PublicSessionSignupPackageMode,
  PublicSessionSignupPackagePhotoType,
  PublicSessionSignupSubmissionFieldsFragment,
} from '../../../../../../../../photographer-panel/src/graphql';
import {
  GetPeopleInGroupGQL,
  PersonToPhotoRelation,
} from '../../../../image-management/graphql';

type SignupCampaign = PublicSessionSignupCampaignFieldsFragment;
type SignupSubmission = PublicSessionSignupSubmissionFieldsFragment;
type SignupSubmissionChild = SignupSubmission['children'][number];

const isString = (value?: string | null): value is string => !!value;

interface ShootSequencePackage {
  publicSessionSignupCampaignPackageId: string | null;
  publicSessionSignupPackageId?: string | null;
  name: string | null;
  description: string | null;
  totalPrice?: number | null;
  requirePrepayment?: boolean | null;
  prepaymentAmount?: number | null;
  estimatedDurationMinutes: number;
  photoType: PublicSessionSignupPackagePhotoType | null;
  averageChildrenCount: number;
  status?: boolean | null;
  order?: number | null;
  portfolioPhotoGroupId?: string | null;
}

export interface FaceToLabelPhoto {
  groupId: string;
  photo: {
    photoId: string;
    width: number;
    height: number;
    originalName?: string | null;
  };
}

export interface PhotoAssignedPerson {
  tabloMemberId: string;
  firstName: string;
  lastName: string;
}

export interface FaceToLabelPhotoViewModel extends FaceToLabelPhoto {
  assignedPeople: PhotoAssignedPerson[];
  assignedLabel: string;
  isAssignedToCurrentMember: boolean;
}

interface FaceToLabelPhotoGridItem {
  photo: FaceToLabelPhotoViewModel;
}

export const buildPhotoAssignedPeopleByPhotoId = (
  relations: PersonToPhotoRelation[],
  tabloMembers: TabloMember[],
): Record<string, PhotoAssignedPerson[]> => {
  const tabloMembersById = new Map(
    tabloMembers.map((member) => [member.tabloMemberId, member]),
  );
  const seenRelations = new Set<string>();

  return relations.reduce<Record<string, PhotoAssignedPerson[]>>(
    (acc, relation) => {
      const photoId = relation.photoId;
      if (!photoId) {
        return acc;
      }

      (relation.people ?? []).filter(isString).forEach((personId) => {
        const relationKey = `${photoId}:${personId}`;
        const member = tabloMembersById.get(personId);
        if (!member || seenRelations.has(relationKey)) {
          return;
        }

        seenRelations.add(relationKey);
        if (!acc[photoId]) {
          acc[photoId] = [];
        }
        acc[photoId].push({
          tabloMemberId: member.tabloMemberId,
          firstName: member.firstName,
          lastName: member.lastName,
        });
      });

      return acc;
    },
    {},
  );
};

export const formatPhotoAssignedPeopleLabel = (
  assignedPeople: PhotoAssignedPerson[],
): string =>
  assignedPeople
    .map((person) => `${person.firstName} ${person.lastName}`.trim())
    .filter(Boolean)
    .join(', ');

export const buildFaceToLabelPhotoViewModels = (
  photos: FaceToLabelPhoto[],
  assignedPeopleByPhotoId: Record<string, PhotoAssignedPerson[]>,
  currentMemberId: string | null,
): FaceToLabelPhotoViewModel[] =>
  photos.map((photo) => {
    const assignedPeople = assignedPeopleByPhotoId[photo.photo.photoId] ?? [];

    return {
      ...photo,
      assignedPeople,
      assignedLabel: formatPhotoAssignedPeopleLabel(assignedPeople),
      isAssignedToCurrentMember: assignedPeople.some(
        (person) => person.tabloMemberId === currentMemberId,
      ),
    };
  });

export const filterFaceToLabelPhotos = (
  photos: FaceToLabelPhotoViewModel[],
  currentMemberId: string | null,
  currentConfig: Record<string, TabloFaceToPerson>,
  showOnlyWithConfig: boolean,
  showAllForCurrentMember: boolean,
): FaceToLabelPhotoViewModel[] => {
  const configuredPhotoIds = Object.values(currentConfig)
    .filter((value) => !!value.photoId)
    .map((value) => value.photoId);
  const photosMatchingConfig = showOnlyWithConfig
    ? photos.filter((photo) => configuredPhotoIds.includes(photo.photo.photoId))
    : photos;

  if (!currentMemberId || showAllForCurrentMember) {
    return photosMatchingConfig;
  }

  const photosAssignedToCurrentMember = photosMatchingConfig.filter((photo) =>
    photo.assignedPeople.some(
      (person) => person.tabloMemberId === currentMemberId,
    ),
  );

  return photosAssignedToCurrentMember.length
    ? photosAssignedToCurrentMember
    : photosMatchingConfig;
};

interface ShootSequencePackageOption {
  value: string;
  label: string;
}

interface ShootSequenceEntry {
  key: string;
  childName: string;
  packageLabel: string | null;
  filterValue: string | null;
  clickedAt: number;
  clickedAtLabel: string;
}

interface ShootSequencePackageItem {
  key: string;
  label: string;
  filterValue: string;
  isFallback: boolean;
}

@Component({
  selector: 'lib-face-to-label-photographer',
  templateUrl: './face-to-label-photographer.component.html',
  styleUrls: ['./face-to-label-photographer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FaceToLabelPhotographerComponent
  extends AliveState
  implements OnInit
{
  photos$ = new BehaviorSubject<FaceToLabelPhoto[]>([]);
  tabloMembers$ = new BehaviorSubject<TabloMember[]>([]);
  @Input() @Required imagesContext: ZalamoObject;
  @Input() @Required contextId;
  @Input() publicSessionSignupCampaignId: string | null = null;
  containerRatio = 1;
  currentFocalPoint = new BehaviorSubject({
    x: 0,
    y: 0,
  });
  dndInProgress$ = new BehaviorSubject(false);
  faceDetectionInProgress$ = new BehaviorSubject(false);
  showOnlyPhotosWithMatchingConfig$ = new UntypedFormControl(false);
  showAllPhotosForCurrentMember = new UntypedFormControl(false);
  showFileNames = new UntypedFormControl(false);
  hasFocalPoint$ = this.currentFocalPoint.pipe(map((v) => !!v.x && !!v.y));
  currentMemberId$ = new BehaviorSubject<string | null>(null);
  currentConfig$ = new BehaviorSubject<Record<string, TabloFaceToPerson>>({});
  currentMember$ = combineLatest([
    this.tabloMembers$,
    this.currentMemberId$,
  ]).pipe(
    map(([tabloMembers, currentMemberId]) =>
      tabloMembers.find((v) => v.tabloMemberId === currentMemberId),
    ),
  );
  pickedPhotoId$ = new BehaviorSubject<{ photoId: string; groupId: string }>(
    null,
  );

  set photos(photos: FaceToLabelPhoto[]) {
    this.photos$.next(photos);
  }

  set tabloMembers(tabloMembers: TabloMember[]) {
    this.tabloMembers$.next(tabloMembers);
  }

  @HostBinding('class.hasCircle') @Input() hasCircle = false;
  memberFirstName = '';
  memberLastName = '';
  currentValue;
  currentCropValue = null;
  saved = new Subject<Record<string, TabloFaceToPerson>>();
  scaleFactor = new UntypedFormControl(1);
  rotation = new UntypedFormControl(0);
  showShootSequencePanel = false;
  shootSequenceLoading$ = new BehaviorSubject(false);
  shootSequenceError$ = new BehaviorSubject<string | null>(null);
  shootSequenceEntries$ = new BehaviorSubject<ShootSequenceEntry[]>([]);
  shootSequencePackageOptions$ = new BehaviorSubject<
    ShootSequencePackageOption[]
  >([]);
  shootSequencePackageControl = new UntypedFormControl(null);
  visibleShootSequenceEntries$ = combineLatest([
    this.shootSequenceEntries$,
    this.shootSequencePackageOptions$,
    this.shootSequencePackageControl.valueChanges.pipe(startWith(null)),
  ]).pipe(
    map(([entries, packageOptions, selectedPackage]) => {
      if (!packageOptions.length) {
        return entries;
      }

      const activePackage =
        selectedPackage &&
        packageOptions.some((item) => item.value === selectedPackage)
          ? selectedPackage
          : (packageOptions[0]?.value ?? null);

      if (!activePackage) {
        return entries;
      }

      return entries.filter((entry) => entry.filterValue === activePackage);
    }),
  );
  private readonly fallbackPackageFilterValue = '__fallback__';
  sourcePhotoGroupIds$ = this.photos$.pipe(
    map((photos) => [
      ...new Set(
        photos
          .map((photo) => photo.groupId)
          .filter((groupId): groupId is string => !!groupId),
      ),
    ]),
    distinctUntilChanged(
      (left, right) =>
        left.length === right.length &&
        left.every((groupId, index) => groupId === right[index]),
    ),
  );
  peopleInSourceGroups$ = this.sourcePhotoGroupIds$.pipe(
    switchMap((photoGroupId) =>
      photoGroupId.length
        ? this.getPeopleInGroupGQL
            .watch({ variables: { photoGroupId } })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const { data } = completeQueryResult(apolloQueryResult);
                return (data?.getPeopleInGroup ?? []).filter(
                  (relation): relation is PersonToPhotoRelation => !!relation,
                );
              }),
              catchError((error) => {
                console.error('Error loading photo person relations', error);
                return of([] as PersonToPhotoRelation[]);
              }),
            )
        : of([] as PersonToPhotoRelation[]),
    ),
    startWith([] as PersonToPhotoRelation[]),
    shareReplay(1),
  );
  assignedPeopleByPhotoId$ = combineLatest([
    this.peopleInSourceGroups$,
    this.tabloMembers$,
  ]).pipe(
    map(([relations, tabloMembers]) =>
      buildPhotoAssignedPeopleByPhotoId(relations, tabloMembers),
    ),
  );
  photosWithAssignedPeople$ = combineLatest([
    this.photos$,
    this.assignedPeopleByPhotoId$,
    this.currentMemberId$,
  ]).pipe(
    map(([photos, assignedPeopleByPhotoId, currentMemberId]) =>
      buildFaceToLabelPhotoViewModels(
        photos,
        assignedPeopleByPhotoId,
        currentMemberId,
      ),
    ),
  );
  photosToShow$ = combineLatest([
    this.photosWithAssignedPeople$,
    this.currentMemberId$,
    this.currentConfig$,
    this.showOnlyPhotosWithMatchingConfig$.valueChanges.pipe(startWith(false)),
    this.showAllPhotosForCurrentMember.valueChanges.pipe(startWith(false)),
  ]).pipe(
    map(
      ([
        photos,
        currentMemberId,
        currentConfig,
        showOnlyWithConfig,
        showAllForCurrentMember,
      ]) =>
        filterFaceToLabelPhotos(
          photos,
          currentMemberId,
          currentConfig,
          showOnlyWithConfig,
          showAllForCurrentMember,
        ),
    ),
  );
  pickedPhoto$ = combineLatest([this.photos$, this.pickedPhotoId$]).pipe(
    map(([photos, pickedPhotoId]) =>
      photos.find((v) => v.photo.photoId === pickedPhotoId?.photoId),
    ),
  );
  completionProgress$ = combineLatest([
    this.tabloMembers$,
    this.currentConfig$,
  ]).pipe(
    map(([tabloMembers, currentConfig]) => {
      if (!tabloMembers || tabloMembers.length === 0) {
        return 0;
      }

      const completedMembers = tabloMembers.filter(
        (member) =>
          currentConfig[member.tabloMemberId] &&
          currentConfig[member.tabloMemberId].photoId,
      );

      return {
        completed: completedMembers.length,
        total: tabloMembers.length,
        percentage: Math.round(
          (completedMembers.length / tabloMembers.length) * 100,
        ),
      };
    }),
  );
  currentPhotoSize = {
    w: 0,
    h: 0,
  };
  get currentCursor() {
    return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='80'><polygon points='0,0 20,0 0,20' fill='blue' /><rect rx='20' width='100%' height='100%' fill='blue' /><text x='50%' y='40%' font-family='sans-serif'  font-size='14' dominant-baseline='middle' text-anchor='middle' fill='white'><![CDATA[ ${this.memberFirstName} ]]></text><text x='50%' y='60%' font-family='sans-serif'  font-size='14' dominant-baseline='middle' text-anchor='middle' fill='white'><![CDATA[ ${this.memberLastName} ]]></text></svg>") 16 0, auto`;
  }

  constructor(
    private ref: ElementRef<HTMLElement>,
    private faceStrongDetectionService: FaceStrongDetectionService,
    private downloader: GetImageUrlService,
    private domSanitizer: DomSanitizer,
    private tabloMemberPhotoPositionService: TabloMemberPhotoPositionService,
    private renderer: Renderer2,
    private getPublicSessionSignupCampaignGQL: GetPublicSessionSignupCampaignGQL,
    private getPublicSessionSignupCampaignSubmissionsGQL: GetPublicSessionSignupCampaignSubmissionsGQL,
    private getPeopleInGroupGQL: GetPeopleInGroupGQL,
  ) {
    super();
  }

  ngOnInit(): void {
    this.faceStrongDetectionService.init();
    void this.loadShootSequence();

    // Load saved positions for all members
    this.subscribeWhileAlive(
      combineLatest([this.tabloMembers$, this.photos$]).pipe(
        filter(
          ([members, photos]) => members?.length > 0 && photos?.length > 0,
        ),
        switchMap(([members, photos]) => {
          const memberIds = members.map((member) => member.tabloMemberId);
          const photoIds = photos.map((photo) => photo.photo.photoId);
          return this.tabloMemberPhotoPositionService
            .getTabloMemberPhotoPositions(memberIds, this.contextId)
            .pipe(
              map((savedPositions: Record<string, TabloFaceToPerson>) => ({
                savedPositions,
                photoIds,
              })),
            );
        }),
        tap(({ savedPositions, photoIds }) => {
          if (Object.keys(savedPositions).length > 0) {
            console.log('Loaded saved positions for members', savedPositions);

            const filteredSavedPositions = Object.keys(savedPositions).reduce<
              Record<string, TabloFaceToPerson>
            >((acc, key) => {
              const value = savedPositions[key];
              if (photoIds.includes(value.photoId)) {
                acc[key] = value;
              }
              return acc;
            }, {});

            // Merge saved positions with current config
            this.currentConfig$.next({
              ...this.currentConfig$.value,
              ...filteredSavedPositions,
            });
          }
        }),
      ),
    );

    this.subscribeWhileAlive(
      combineLatest([this.tabloMembers$, this.currentConfig$]).pipe(
        debounceTime(100),
        tap(([tabloMembers, currentConfig]) => {
          if (!this.currentMemberId$.value && tabloMembers.length > 0) {
            this.showMember(tabloMembers[0].tabloMemberId);
          }
        }),
      ),
      fromEvent(document, 'keydown').pipe(
        tap((x: KeyboardEvent) => console.log({ code: x.code, type: x.type })),
        filter((event: KeyboardEvent) => event.code === 'Space'),
        tap(() => {
          this.saveCurrent();
        }),
      ),
    );
  }

  get hasShootSequencePanel() {
    return (
      !!this.publicSessionSignupCampaignId &&
      `${this.imagesContext || ''}` === 'PublicSession'
    );
  }

  get hasShootSequencePackageFilter() {
    return this.shootSequencePackageOptions$.value.length > 0;
  }

  toggleShootSequencePanel() {
    this.showShootSequencePanel = !this.showShootSequencePanel;
  }

  save() {
    // this.saved.next({
    //   crop: this.currentValue,
    //   photoId: this.pickedPhotoId$.value,
    // });
  }

  onDrag($event: DragEvent) {
    console.log($event);
    $event.preventDefault();
    $event.stopPropagation();
  }

  onDragStart($event: DragEvent, photo: FaceToLabelPhotoGridItem) {
    this.dndInProgress$.next(true);
    console.log($event);
    console.log(photo.photo.photo.photoId);
    $event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        photoId: photo.photo.photo.photoId,
        groupId: photo.photo.groupId,
        width: photo.photo.photo.width,
        height: photo.photo.photo.height,
      }),
    );
    $event.dataTransfer.effectAllowed = 'link';

    return true;
  }

  onDragOver($event: DragEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    $event.dataTransfer.dropEffect = 'link';
  }

  async onDrop($event: DragEvent) {
    $event.preventDefault();

    console.log($event);
    const data = $event.dataTransfer.getData('text/plain');
    const parsed = JSON.parse(data);
    const url = await firstValueFrom(
      this.downloader.transform(
        {
          photoId: parsed.photoId,
        },
        parsed.groupId,
        'photographer',
        this.imagesContext,
        this.contextId,
        600,
      ),
    );
    this.currentPhotoSize = {
      w: parsed.width,
      h: parsed.height,
    };

    const detectedUrl =
      `${url}`.match(/blob:https?:\/\/[^\s)]+/)?.[0] ?? `${url}`;

    const picked =
      await this.faceStrongDetectionService.detectFaces(detectedUrl);
    console.log(picked);
    console.log({
      scale: 1 / picked.faces[0].boxRaw[2],
      boxRaw: picked.faces[0].boxRaw,
    });
    if (picked.faces.length === 1) {
      const centerPointInPercentages = {
        x:
          (picked.gravityCenter[0] +
            (picked.gravityCenter[2] - picked.gravityCenter[0]) / 2) *
          100,
        y:
          (picked.gravityCenter[1] +
            (picked.gravityCenter[3] - picked.gravityCenter[1]) / 2) *
          100,
      };
      this.currentFocalPoint.next(centerPointInPercentages);
      this.scaleFactor.setValue(
        Math.max(1 / (picked.faces[0].boxRaw[2] * 1.6), 1),
      );
    }
    this.pickedPhotoId$.next(JSON.parse(data));
  }

  pointUpdatedByHand($event: { x: number; y: number }) {
    this.currentFocalPoint.next({
      x: $event.x,
      y: $event.y,
    });
  }

  async saveCurrent() {
    const current = await firstValueFrom(
      combineLatest([
        this.pickedPhotoId$,
        this.currentFocalPoint,
        this.currentMemberId$,
      ]),
    );

    const { x, y } = current[1];
    if (!current[0]) {
      return;
    }
    const { photoId, groupId } = current[0];
    const tabloMemberId = current[2];
    if (!tabloMemberId) {
      return;
    }
    if (!photoId) {
      return;
    }

    const photoPosition = {
      x,
      y,
      photoId,
      groupId,
      width: this.currentPhotoSize.w,
      height: this.currentPhotoSize.h,
      scale: this.scaleFactor.value,
      rotation: this.rotation.value,
    };

    // Save to local config
    this.currentConfig$.next({
      ...this.currentConfig$.value,
      [tabloMemberId]: photoPosition,
    });

    // Save to database for future reuse
    this.tabloMemberPhotoPositionService
      .saveTabloMemberPhotoPosition(
        tabloMemberId,
        photoPosition,
        this.contextId,
      )
      .subscribe(
        (result) => {
          console.log('Photo position saved to database', result);
        },
        (error) => {
          console.error('Error saving photo position to database', error);
        },
      );

    const allMembers = await firstValueFrom(this.tabloMembers$);
    const firstMemberWithoutConfig = allMembers.find(
      (v) =>
        !this.currentConfig$.value[v.tabloMemberId] &&
        v.tabloMemberId !== tabloMemberId,
    );
    if (firstMemberWithoutConfig) {
      this.showMember(firstMemberWithoutConfig.tabloMemberId);
      this.pickedPhotoId$.next(null);
    } else {
      // this.currentMemberId$.next(null);
    }
  }

  async showMember(tabloMemberId: string, fromMenu = false) {
    if (fromMenu) {
      this.saveCurrent();
    }
    this.currentMemberId$.next(tabloMemberId);
    const current = this.currentConfig$.value[tabloMemberId];
    const member = (await firstValueFrom(this.tabloMembers$)).find(
      (v) => v.tabloMemberId === tabloMemberId,
    );
    this.memberFirstName = member.firstName;
    this.memberLastName = member.lastName;

    if (current) {
      // Use the current configuration if it exists
      this.currentPhotoSize = {
        w: current.width,
        h: current.height,
      };
      this.currentFocalPoint.next({
        x: current.x,
        y: current.y,
      });
      this.pickedPhotoId$.next({
        photoId: current.photoId,
        groupId: current.groupId,
      });
      this.scaleFactor.setValue(current.scale);
      this.rotation.setValue(current.rotation);
    } else {
      // If no current configuration, try to get it from the database
      const [savedPosition, photosToShow] = await firstValueFrom(
        combineLatest([
          this.tabloMemberPhotoPositionService.getTabloMemberPhotoPosition(
            tabloMemberId,
            this.contextId,
          ),
          this.photos$,
        ]),
      );

      if (
        savedPosition &&
        savedPosition.photoId &&
        photosToShow.some((v) => v.photo.photoId === savedPosition.photoId)
      ) {
        console.log(
          'Found saved position for member',
          tabloMemberId,
          savedPosition,
        );

        // Update the current configuration with the saved position
        this.currentConfig$.next({
          ...this.currentConfig$.value,
          [tabloMemberId]: savedPosition,
        });

        // Set the UI elements with the saved position
        this.currentPhotoSize = {
          w: savedPosition.width,
          h: savedPosition.height,
        };
        this.currentFocalPoint.next({
          x: savedPosition.x,
          y: savedPosition.y,
        });
        this.pickedPhotoId$.next({
          photoId: savedPosition.photoId,
          groupId: savedPosition.groupId,
        });
        this.scaleFactor.setValue(savedPosition.scale);
        this.rotation.setValue(savedPosition.rotation);
      } else {
        // If no saved position, reset to defaults
        this.currentFocalPoint.next({
          x: 0,
          y: 0,
        });
        this.pickedPhotoId$.next(null);
        this.scaleFactor.setValue(1);
        this.rotation.setValue(0);
      }
    }
  }

  removePhoto() {
    const memberId = this.currentMemberId$.value;

    // Remove from local config
    this.currentConfig$.next({
      ...this.currentConfig$.value,
      [memberId]: {
        x: 0,
        y: 0,
        photoId: null,
        groupId: null,
        width: 0,
        height: 0,
        scale: 1,
        rotation: 0,
      },
    });

    // Remove from database
    this.tabloMemberPhotoPositionService
      .deleteTabloMemberPhotoPosition(memberId, this.contextId)
      .subscribe(
        (result) => {
          console.log('Photo position deleted from database', result);
        },
        (error) => {
          console.error('Error deleting photo position from database', error);
        },
      );

    this.currentFocalPoint.next({
      x: 0,
      y: 0,
    });
    this.pickedPhotoId$.next(null);
    this.scaleFactor.setValue(1);
  }

  async photoClick(photo: FaceToLabelPhoto) {
    const url = await firstValueFrom(
      this.downloader.transform(
        {
          photoId: photo.photo.photoId,
        },
        photo.groupId,
        'photographer',
        this.imagesContext,
        this.contextId,
        600,
      ),
    );
    this.currentPhotoSize = {
      w: photo.photo.width,
      h: photo.photo.height,
    };

    this.faceDetectionInProgress$.next(true);

    try {
      const picked = await firstValueFrom(
        this.faceStrongDetectionService.detectFacesByPhotoId(
          photo.photo.photoId,
          photo.groupId,
          1,
          this.contextId,
          photo.photo,
          this.imagesContext,
        ),
      );
      this.rotation.setValue(0);
      console.log(picked);
      if (picked.faces.length === 1) {
        const centerPointInPercentages = {
          x:
            (picked.gravityCenter[0] +
              (picked.gravityCenter[2] - picked.gravityCenter[0]) / 2) *
            100,
          y:
            (picked.gravityCenter[1] +
              (picked.gravityCenter[3] - picked.gravityCenter[1]) / 2) *
            100,
        };
        this.currentFocalPoint.next(centerPointInPercentages);

        //calculate face rotation

        const face = picked.faces[0];
        const roll = face.rotation.angle.roll; // radians (-π … +π)

        // If boxRaw is [x1, y1, x2, y2]  → get width & height
        // If it is [x,  y,  w,  h]        → drop the subtraction
        const [bx, by, bw, bh] = face.boxRaw; // bx,by are top-left
        const w = bw; // width already given
        const h = bh; // height already given

        // --- rotation-aware size ---------------------------------------------------
        const absCos = Math.abs(Math.cos(roll));
        const absSin = Math.abs(Math.sin(roll));

        /**
         * width of the *smallest* rectangle that encloses the rotated face box
         * w' = |w·cosθ| + |h·sinθ|
         * h' = |w·sinθ| + |h·cosθ|   // —useful if you ever need the height too
         */
        const effectiveWidth = w * absCos + h * absSin;

        // (alternatively use the diagonal, which is rotation-independent)
        // const effectiveWidth = Math.hypot(w, h);

        // --- final scale -----------------------------------------------------------
        this.scaleFactor.setValue(Math.max(1 / (effectiveWidth * 1.8), 1));
        // const rotationInDegrees = (roll * 180) / Math.PI;
        // this.rotation.setValue(-rotationInDegrees / 2);
      }
      this.pickedPhotoId$.next({
        photoId: photo.photo.photoId,
        groupId: photo.groupId,
      });
    } catch (error) {
      console.error('Error during face detection:', error);
    } finally {
      this.faceDetectionInProgress$.next(false);
    }
  }

  async complete() {
    await this.saveCurrent();
    this.saved.next(this.currentConfig$.value);
  }

  private async loadShootSequence() {
    if (!this.hasShootSequencePanel) {
      this.shootSequenceEntries$.next([]);
      this.shootSequencePackageOptions$.next([]);
      return;
    }

    this.shootSequenceLoading$.next(true);
    this.shootSequenceError$.next(null);

    try {
      const [campaign, submissions] = await Promise.all([
        firstValueFrom(
          this.getPublicSessionSignupCampaignGQL
            .watch({
              variables: {
                publicSessionSignupCampaignHash:
                  this.publicSessionSignupCampaignId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const { data } = completeQueryResult(apolloQueryResult);
                return data?.getPublicSessionSignupCampaign ?? null;
              }),
            ),
        ),
        firstValueFrom(
          this.getPublicSessionSignupCampaignSubmissionsGQL
            .watch({
              variables: {
                publicSessionSignupCampaignId:
                  this.publicSessionSignupCampaignId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const { data } = completeQueryResult(apolloQueryResult);
                return (
                  data?.getPublicSessionSignupCampaignSubmissions ?? []
                ).filter(Boolean) as SignupSubmission[];
              }),
            ),
        ),
      ]);

      const currentGroup = (campaign?.groups ?? []).find(
        (group) => group?.linkedPublicSessionId === this.contextId,
      );

      if (!campaign || !currentGroup?.publicSessionSignupCampaignGroupId) {
        this.shootSequenceEntries$.next([]);
        this.shootSequencePackageOptions$.next([]);
        return;
      }

      const activePackages = this.buildActivePackages(campaign);
      const activePackagesMap = new Map(
        activePackages.map((pkg) => [
          pkg.publicSessionSignupCampaignPackageId,
          pkg,
        ]),
      );
      const hasRealPackages =
        campaign.packageMode !== PublicSessionSignupPackageMode.None &&
        activePackages.length > 0;
      const defaultSinglePackage =
        campaign.packageMode === PublicSessionSignupPackageMode.None
          ? null
          : activePackages.length === 1
            ? activePackages[0]
            : null;
      const fallbackPackageSeen = { value: false };
      const packageOptions = activePackages.map((pkg) => ({
        value: pkg.publicSessionSignupCampaignPackageId,
        label: pkg.name || 'Pakiet',
      }));
      const entries: ShootSequenceEntry[] = [];

      (submissions ?? []).forEach((submission) => {
        (submission.children ?? [])
          .filter(
            (child) =>
              child?.publicSessionSignupCampaignGroupId ===
              currentGroup.publicSessionSignupCampaignGroupId,
          )
          .forEach((child) => {
            const childName =
              `${child?.firstName || ''} ${child?.lastName || ''}`.trim() ||
              'Bez imienia';
            const packageItems = this.buildShootSequencePackageItems(
              child,
              childName,
              campaign,
              activePackagesMap,
              defaultSinglePackage,
              fallbackPackageSeen,
            );
            const checklistByKey = new Map(
              (child?.sessionChecklist ?? [])
                .filter((item) => !!item?.key)
                .map((item) => [item.key, item]),
            );

            packageItems.forEach((packageItem) => {
              const checklistItem = checklistByKey.get(packageItem.key);
              if (!checklistItem?.checked || !checklistItem?.updatedAt) {
                return;
              }

              entries.push({
                key: packageItem.key,
                childName,
                packageLabel: packageItem.isFallback ? null : packageItem.label,
                filterValue: packageItem.filterValue,
                clickedAt: Number(checklistItem.updatedAt),
                clickedAtLabel: this.formatShootSequenceDateTime(
                  Number(checklistItem.updatedAt),
                ),
              });
            });
          });
      });

      if (fallbackPackageSeen.value) {
        packageOptions.push({
          value: this.fallbackPackageFilterValue,
          label: 'Brak pakietu',
        });
      }

      entries.sort((left, right) => {
        if (left.clickedAt !== right.clickedAt) {
          return left.clickedAt - right.clickedAt;
        }

        if (left.childName !== right.childName) {
          return left.childName.localeCompare(right.childName, 'pl');
        }

        return (left.packageLabel || '').localeCompare(
          right.packageLabel || '',
          'pl',
        );
      });

      this.shootSequenceEntries$.next(entries);
      this.shootSequencePackageOptions$.next(
        hasRealPackages ? packageOptions : [],
      );

      if (!hasRealPackages || !packageOptions.length) {
        this.shootSequencePackageControl.setValue(null, { emitEvent: false });
      } else {
        const currentValue = this.shootSequencePackageControl.value;
        const nextValue = packageOptions.some(
          (option) => option.value === currentValue,
        )
          ? currentValue
          : packageOptions[0].value;
        this.shootSequencePackageControl.setValue(nextValue, {
          emitEvent: false,
        });
      }
    } catch (error) {
      this.shootSequenceError$.next(
        error?.message || 'Nie udało się pobrać kolejności wykonania zdjęć.',
      );
      this.shootSequenceEntries$.next([]);
      this.shootSequencePackageOptions$.next([]);
    } finally {
      this.shootSequenceLoading$.next(false);
    }
  }

  private buildActivePackages(
    campaign: SignupCampaign,
  ): ShootSequencePackage[] {
    const packageAssignmentsByPackageId = new Map(
      (campaign?.packageAssignments ?? [])
        .filter((assignment) => !!assignment.publicSessionSignupPackageId)
        .map((assignment) => [
          assignment.publicSessionSignupPackageId,
          assignment,
        ]),
    );

    const normalizedPackages = (campaign?.packages ?? []).map((pkg) => {
      const matchingAssignment = packageAssignmentsByPackageId.get(
        pkg.publicSessionSignupPackageId,
      );

      return {
        ...pkg,
        name: pkg.name || matchingAssignment?.name || '',
        description: pkg.description || matchingAssignment?.description || '',
        photoType:
          pkg.photoType ||
          matchingAssignment?.photoType ||
          PublicSessionSignupPackagePhotoType.Individual,
        averageChildrenCount: Number(
          pkg.averageChildrenCount ??
            matchingAssignment?.averageChildrenCount ??
            1,
        ),
        estimatedDurationMinutes: Number(
          pkg.estimatedDurationMinutes ??
            matchingAssignment?.estimatedDurationMinutes ??
            0,
        ),
      };
    });

    const synthesizedPackages = normalizedPackages.length
      ? normalizedPackages
      : (campaign?.packageAssignments ?? []).map((assignment, index) => ({
          publicSessionSignupCampaignPackageId:
            assignment.publicSessionSignupCampaignPackageAssignmentId,
          publicSessionSignupPackageId: assignment.publicSessionSignupPackageId,
          name: assignment.name || '',
          description: assignment.description || '',
          totalPrice: Number(assignment.totalPrice ?? 0),
          requirePrepayment: assignment.requirePrepayment === true,
          prepaymentAmount: Number(assignment.prepaymentAmount ?? 0),
          estimatedDurationMinutes: Number(
            assignment.estimatedDurationMinutes ?? 0,
          ),
          photoType:
            assignment.photoType ||
            PublicSessionSignupPackagePhotoType.Individual,
          averageChildrenCount: Number(assignment.averageChildrenCount ?? 1),
          status: assignment.status !== false,
          order:
            typeof assignment.order === 'number' ? assignment.order : index,
          portfolioPhotoGroupId: assignment.portfolioPhotoGroupId ?? null,
        }));

    return synthesizedPackages
      .filter((pkg) => pkg.status !== false)
      .sort((left, right) => Number(left.order) - Number(right.order));
  }

  private getPackagesForChild(
    child: SignupSubmissionChild,
    campaign: SignupCampaign,
    activePackagesMap: Map<string | null, ShootSequencePackage>,
    defaultSinglePackage: ShootSequencePackage | null,
  ): ShootSequencePackage[] {
    if (child?.selectedPackages?.length) {
      return child.selectedPackages.map((pkg) => {
        const activePackage = activePackagesMap.get(
          pkg.publicSessionSignupCampaignPackageId,
        );

        return {
          ...activePackage,
          ...pkg,
          name: pkg?.name || activePackage?.name || '',
          description: activePackage?.description || '',
          photoType:
            pkg?.photoType ||
            activePackage?.photoType ||
            PublicSessionSignupPackagePhotoType.Individual,
          averageChildrenCount: Number(
            pkg?.averageChildrenCount ??
              activePackage?.averageChildrenCount ??
              1,
          ),
          estimatedDurationMinutes: Number(
            pkg?.estimatedDurationMinutes ??
              activePackage?.estimatedDurationMinutes ??
              0,
          ),
        };
      });
    }

    if (campaign?.packageMode === PublicSessionSignupPackageMode.None) {
      return [];
    }

    return defaultSinglePackage ? [defaultSinglePackage] : [];
  }

  private buildShootSequencePackageItems(
    child: SignupSubmissionChild,
    childName: string,
    campaign: SignupCampaign,
    activePackagesMap: Map<string | null, ShootSequencePackage>,
    defaultSinglePackage: ShootSequencePackage | null,
    fallbackPackageSeen: { value: boolean },
  ): ShootSequencePackageItem[] {
    const packages = this.getPackagesForChild(
      child,
      campaign,
      activePackagesMap,
      defaultSinglePackage,
    );

    if (packages.length) {
      return packages.map((pkg, index) => ({
        key: `${child?.publicSessionSignupSubmissionChildId || 'child'}-${
          pkg?.publicSessionSignupCampaignPackageId || pkg?.name || index
        }`,
        label: pkg?.name || 'Pakiet',
        filterValue: pkg?.publicSessionSignupCampaignPackageId || `${index}`,
        isFallback: false,
      }));
    }

    fallbackPackageSeen.value = true;

    return [
      {
        key: `${
          child?.publicSessionSignupSubmissionChildId || 'child'
        }-fallback`,
        label:
          campaign?.packageMode === PublicSessionSignupPackageMode.None
            ? childName
            : 'Brak pakietu',
        filterValue: this.fallbackPackageFilterValue,
        isFallback: true,
      },
    ];
  }

  private formatShootSequenceDateTime(value: number) {
    if (!value) {
      return '';
    }

    return new Date(value * 1000).toLocaleString('pl-PL');
  }
}
